/**
 * App-level team queue goals (SaaS Foundation GET/PATCH).
 * Rows are scoped by rcm_service_code; PATCH replaces the document with merged goals across services.
 *
 * Role in the platform: goals define throughput targets (human + Kai) per RCM service;
 * progress is measured against queue activity. Task Manager holds assignable work; the
 * Agentic Work Queue shows Task Manager + RCM-native queues together. See
 * `workqueue/rcmWorkDomain.js` for the end-to-end model.
 */

/** Ports where the AgenticRCM (or similar) shell nginx proxies `/api` → SaaS Foundation. */
const _SHELL_PORTS_WITH_SAAS_API_PROXY = new Set(['3160', '3161', '80', '443', '']);

/** RCM / practice MFE ports — not SaaS Foundation; REACT_APP_SAAS_API_URL is often mis-set to these. */
const _TYPICAL_RCM_FRONTEND_PORTS = new Set([
  '3000', '3010', '3110', '3150', '3151', '3152', '3153', '3154', '3155', '3156', '3157', '3158',
  '3200', '3204', '3205', '3208',
]);

/**
 * If REACT_APP_SAAS_API_URL points at an RCM SPA port (e.g. :3110), /api/v1/organizations/* hits the SPA/nginx
 * → 404. Rewrite to same host :8001 (SaaS Foundation). Applies for any path on that port (build often adds /app paths).
 */
function sanitizeSaaSBaseUrl(candidate) {
  if (!candidate) return candidate;
  try {
    const u = new URL(candidate, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    // Prefer u.port; default HTTP/HTTPS ports only when port is omitted (not when an MFE uses a non-default port).
    const p = u.port !== '' ? String(u.port) : u.protocol === 'https:' ? '443' : '80';
    if (p === '8001' || p === '80' || p === '443') return candidate.replace(/\/+$/, '');
    if (_TYPICAL_RCM_FRONTEND_PORTS.has(p)) {
      return `${u.protocol}//${u.hostname}:8001`;
    }
  } catch {
    /* ignore */
  }
  return String(candidate).replace(/\/+$/, '');
}

/**
 * Second pass: REACT_APP_SAAS_API_URL sometimes points at an MFE origin (:3110, :3160, …) but
 * URL parsing still yields a frontend port → team-queue-goals hits the SPA (404). Force :8001.
 */
function ensureSaaSFoundationOrigin(url) {
  const once = sanitizeSaaSBaseUrl(url);
  try {
    const u = new URL(once, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const portStr = u.port !== '' ? String(u.port) : u.protocol === 'https:' ? '443' : '80';
    if (_TYPICAL_RCM_FRONTEND_PORTS.has(portStr)) {
      return `${u.protocol}//${u.hostname}:8001`;
    }
  } catch {
    /* ignore */
  }
  return once;
}

/**
 * Final pass: team-queue-goals must hit SaaS Foundation (:8001), never an RCM/MFE static port (e.g. Patient Account :3110).
 * Call on every resolved base so mis-set env or older bundles cannot leave requests pointed at the SPA (→ 404 HTML/JSON).
 */
function _isLocalhostHostname() {
  if (typeof window === 'undefined' || !window.location?.hostname) return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

export function rewriteMfeOriginToSaaSFoundationBase(input) {
  /** Shells that proxy `/api` on the same origin as the SPA (e.g. AgenticRcmAgency AuthContext uses `/api/v1`). */
  if (process.env.REACT_APP_SAAS_RELATIVE_API === 'true' && !_isLocalhostHostname()) {
    if (input != null && String(input).trim()) return String(input).trim().replace(/\/+$/, '');
    if (typeof window !== 'undefined' && window.location?.origin) {
      return String(window.location.origin).replace(/\/+$/, '');
    }
    return 'http://localhost:8001';
  }
  if (input == null || String(input).trim() === '') {
    if (typeof window !== 'undefined' && window.location?.hostname) {
      return rewriteMfeOriginToSaaSFoundationBase(window.location.origin);
    }
    return 'http://localhost:8001';
  }
  const s = String(input).trim().replace(/\/+$/, '');
  try {
    const u = new URL(s, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const portStr = u.port !== '' ? String(u.port) : u.protocol === 'https:' ? '443' : '80';
    if (_TYPICAL_RCM_FRONTEND_PORTS.has(portStr)) {
      return `${u.protocol}//${u.hostname}:8001`;
    }
    return `${u.protocol}//${u.hostname}${u.port ? `:${u.port}` : ''}`;
  } catch {
    return s;
  }
}

/**
 * Per-customer subdomain deployment: the shell is `<customer>.<apex>` and each
 * service is `<slug>.<customer>.<apex>`. A service subdomain is ALSO on :443 (empty
 * port), so port alone cannot distinguish it from the shell — using same-origin there
 * sends SaaS calls to the service's own backend, which 404s on /organizations/*.
 * Returns the shell origin when we're on a service subdomain, else null.
 */
function _shellOriginIfServiceSubdomain() {
  if (typeof window === 'undefined' || !window.location) return null;
  const { protocol, hostname } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  const isBareIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
  if (isLocal || isBareIp) return null;
  const labels = hostname.split('.');
  // <slug>.<customer>.<apex> → 4+ labels. The shell is one label up.
  if (labels.length < 4) return null;
  return `${protocol}//${labels.slice(1).join('.')}`;
}

export function getSaaSApiBaseUrl() {
  /**
   * Match login/session API: same browser origin + nginx proxy to SaaS Foundation.
   * Shells on :3160 (and remote 80/443) proxy `/api` → SaaS — prefer same-origin so JWT cookies
   * and Authorization work without opening :8001 on the host.
   */
  // Must precede the same-origin branches below: on a service subdomain the shell,
  // not this app, is the one that proxies /api → SaaS.
  // BUT an explicit operator-configured URL always wins over this inference, so only
  // apply it when no REACT_APP_SAAS_* override is set (that override is handled below).
  const _explicitSaasEnv =
    process.env.REACT_APP_SAAS_API_URL || process.env.REACT_APP_SAAS_FOUNDATION_URL;
  if (!(_explicitSaasEnv && String(_explicitSaasEnv).trim())) {
    const shellOrigin = _shellOriginIfServiceSubdomain();
    if (shellOrigin) return shellOrigin;
  }

  if (
    process.env.REACT_APP_SAAS_RELATIVE_API === 'true' &&
    typeof window !== 'undefined' &&
    window.location?.origin &&
    !_isLocalhostHostname()
  ) {
    return String(window.location.origin).replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port } = window.location;
    const p = port || '';
    // AgenticRCM shell (localhost:3160 or production :80/:443): use same-origin /api proxy
    if (_SHELL_PORTS_WITH_SAAS_API_PROXY.has(p)) {
      return String(window.location.origin).replace(/\/+$/, '');
    }
  }

  const envRaw = process.env.REACT_APP_SAAS_API_URL || process.env.REACT_APP_SAAS_FOUNDATION_URL;
  const env = envRaw && String(envRaw).trim() ? envRaw : null;
  let resolved;
  if (env) {
    let base = String(env)
      .split('#')[0]
      .trim()
      .replace(/\/+$/, '');
    if (base.endsWith('/api/v1')) base = base.slice(0, -7);
    resolved = ensureSaaSFoundationOrigin(base);
  } else if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Standalone MFE (e.g. Denial :3154) — SaaS on host :8001
      resolved = `${protocol}//${hostname}:8001`;
    } else {
      const p = port || '';
      if (!p || p === '80' || p === '443') {
        resolved = ensureSaaSFoundationOrigin(window.location.origin);
      } else {
        resolved = ensureSaaSFoundationOrigin(`${protocol}//${hostname}:${p}`);
      }
    }
  } else {
    resolved = 'http://localhost:8001';
  }
  return rewriteMfeOriginToSaaSFoundationBase(resolved);
}

/** Safe parse when SPA fallback returns 200 + HTML instead of JSON. Never throws. */
export async function parseJsonResponseBody(response) {
  try {
    const text = await response.text();
    if (!text || !text.trim()) return null;
    const t = text.trim();
    if (t.startsWith('<') || t.startsWith('<!')) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  } catch {
    return null;
  }
}

/** Dispatched when embedded shell stores org/JWT after postMessage (see DenialManagement index.js). */
export const SAAS_AUTH_UPDATED_EVENT = 'saas-auth-updated';

/** Dispatched after team-queue-goals PATCH so shell stats / metrics panels refresh. */
export const TEAM_QUEUE_GOALS_CHANGED_EVENT = 'team-queue-goals-changed';

export function dispatchSaaSAuthUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SAAS_AUTH_UPDATED_EVENT));
  }
}

export function dispatchTeamQueueGoalsChanged(detail = {}) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TEAM_QUEUE_GOALS_CHANGED_EVENT, { detail }));
  }
}

/** Subscribe to refetch SaaS Foundation calls after iframe auth sync. */
export function subscribeSaaSAuthUpdated(handler) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(SAAS_AUTH_UPDATED_EVENT, handler);
  return () => window.removeEventListener(SAAS_AUTH_UPDATED_EVENT, handler);
}

export function subscribeTeamQueueGoalsChanged(handler) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(TEAM_QUEUE_GOALS_CHANGED_EVENT, handler);
  return () => window.removeEventListener(TEAM_QUEUE_GOALS_CHANGED_EVENT, handler);
}

/** JWT for SaaS Foundation; prefer URL (?auth_token=) when embedded so goals load before localStorage sync. */
export function getAuthTokenForSaaS() {
  if (typeof window === 'undefined') return '';
  try {
    const q = new URLSearchParams(window.location.search);
    const fromUrl = q.get('auth_token') || q.get('token');
    if (fromUrl) return String(fromUrl).trim();
  } catch (_) {
    /* ignore */
  }
  return (
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('access_token') ||
    ''
  );
}

/**
 * @param {object} g — API goal row (snake_case)
 * @returns {object} panel row (camelCase)
 */
export function saasGoalToPanelRow(g) {
  if (!g || typeof g !== 'object') return null;
  return {
    id: g.id,
    assigneeType: g.assignee_type === 'kai' ? 'kai' : 'human',
    assigneeUserId: g.assignee_user_id || null,
    memberLabel: g.assignee_type === 'kai' ? '' : String(g.member_label || '').trim(),
    period: g.period || 'daily',
    targetCount: Math.max(0, Number(g.target_count) || 0),
    rangeStart: g.range_start ? String(g.range_start).slice(0, 10) : '',
    rangeEnd: g.range_end ? String(g.range_end).slice(0, 10) : '',
  };
}

/**
 * @param {object} row — panel row
 * @param {string} rcmServiceCode — e.g. claims_processing
 * @returns {object} API goal payload
 */
export function panelRowToSaaSGoal(row, rcmServiceCode) {
  return {
    id: row.id,
    rcm_service_code: rcmServiceCode,
    assignee_type: row.assigneeType === 'kai' ? 'kai' : 'human',
    assignee_user_id: row.assigneeUserId || null,
    member_label: row.assigneeType === 'kai' ? '' : String(row.memberLabel || '').trim(),
    period: ['daily', 'weekly', 'date_range'].includes(row.period) ? row.period : 'daily',
    target_count: Math.max(0, parseInt(row.targetCount, 10) || 0),
    range_start: row.period === 'date_range' && row.rangeStart ? String(row.rangeStart).slice(0, 10) : null,
    range_end: row.period === 'date_range' && row.rangeEnd ? String(row.rangeEnd).slice(0, 10) : null,
  };
}

export async function fetchTeamQueueGoalsFull(organizationId) {
  const token = getAuthTokenForSaaS();
  if (!token || !organizationId) return [];
  const base = getSaaSApiBaseUrl();
  const res = await fetch(`${base}/api/v1/organizations/${organizationId}/team-queue-goals`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (res.status === 401 || res.status === 403) {
    console.warn(
      '[teamQueueGoals] unauthorized — ensure JWT is present (auth_token) and SaaS base is',
      base
    );
    return [];
  }
  if (!res.ok) return [];
  const data = await parseJsonResponseBody(res);
  if (!data) return [];
  return Array.isArray(data.goals) ? data.goals : [];
}

/**
 * One-time migrate: if SaaS has no rows for this service, push legacy Denial/settings goals.
 * @returns {Promise<object[]>} panel rows after migrate (may be empty)
 */
export async function migrateLegacyGoalsToSaaS(organizationId, rcmServiceCode, legacyPanelRows) {
  if (!organizationId || !rcmServiceCode) return [];
  const existing = await fetchTeamQueueGoalsForService(organizationId, rcmServiceCode);
  if (Array.isArray(existing) && existing.length > 0) {
    return existing.map(saasGoalToPanelRow).filter(Boolean);
  }
  const legacy = Array.isArray(legacyPanelRows) ? legacyPanelRows : [];
  if (legacy.length === 0) return [];
  const apiGoals = legacy
    .map((row) => {
      const normalized = {
        id: row.id,
        assigneeType: row.assigneeType === 'kai' ? 'kai' : 'human',
        memberLabel: row.assigneeType === 'kai' ? '' : String(row.memberLabel || '').trim(),
        assigneeUserId: row.assigneeUserId || null,
        period: ['daily', 'weekly', 'date_range'].includes(row.period) ? row.period : 'daily',
        targetCount: Math.max(0, parseInt(row.targetCount, 10) || 0),
        rangeStart: row.rangeStart || '',
        rangeEnd: row.rangeEnd || '',
      };
      if (normalized.assigneeType !== 'kai' && !normalized.memberLabel) return null;
      return panelRowToSaaSGoal(normalized, rcmServiceCode);
    })
    .filter(Boolean);
  if (apiGoals.length === 0) return [];
  try {
    await patchTeamQueueGoalsForService(organizationId, rcmServiceCode, apiGoals);
    dispatchTeamQueueGoalsChanged({ organizationId, rcmServiceCode, migrated: true });
    return apiGoals.map(saasGoalToPanelRow).filter(Boolean);
  } catch (e) {
    console.warn('[teamQueueGoals] migrate legacy → SaaS failed', e);
    return legacy;
  }
}

/**
 * @param {string} organizationId
 * @param {string} rcmServiceCode
 * @returns {Promise<object[]>} goals for this service only
 */
export async function fetchTeamQueueGoalsForService(organizationId, rcmServiceCode) {
  const all = await fetchTeamQueueGoalsFull(organizationId);
  return all.filter((g) => (g.rcm_service_code || '') === rcmServiceCode);
}

/**
 * @param {string} organizationId
 * @param {object[]} goals — full merged list
 */
export async function patchTeamQueueGoals(organizationId, goals) {
  const token = getAuthTokenForSaaS();
  if (!token || !organizationId) throw new Error('Not authenticated or missing organization');
  const base = getSaaSApiBaseUrl();
  const res = await fetch(`${base}/api/v1/organizations/${organizationId}/team-queue-goals`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ goals }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || `PATCH team-queue-goals failed: ${res.status}`);
  }
  const json = await res.json();
  dispatchTeamQueueGoalsChanged({ organizationId });
  return json;
}

/**
 * Replace goals for one rcm_service_code; keep all other services' rows.
 * @param {string} organizationId
 * @param {string} rcmServiceCode
 * @param {object[]} serviceRows — API-shaped goals for this service (panelRowToSaaSGoal output)
 */
export async function patchTeamQueueGoalsForService(organizationId, rcmServiceCode, serviceRows) {
  const existing = await fetchTeamQueueGoalsFull(organizationId);
  const other = (existing || []).filter((g) => (g.rcm_service_code || '') !== rcmServiceCode);
  return patchTeamQueueGoals(organizationId, [...other, ...serviceRows]);
}
